import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { Id } from "@teachloop/backend/_generated/dataModel";
import * as libphonenumber from "google-libphonenumber";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Pressable, ScrollView, Text, View } from "react-native";
import Loader from "@/elements/components/shared/loader";
import ResponsiveContent from "@/elements/components/shared/responsive-content";
import { useSheet } from "@/elements/components/shared/sheet-provider";
import type {
  TimezoneSearchSheetPayload,
  TimezoneSearchSheetResult,
} from "@/elements/components/sheets/timezone-search-sheet";
import { AppBar } from "@/elements/components/ui/app-bar";
import { Button } from "@/elements/components/ui/button";
import { Input } from "@/elements/components/ui/input";
import { Tag } from "@/elements/components/ui/tag";
import { api } from "@/elements/components/utils/convex-api";
import goBack from "@/elements/components/utils/go-back";
import { contactSupport } from "@/elements/components/utils/helpers";
import { SheetIds } from "@/elements/components/utils/sheet-ids";
import useEditableFields from "@/elements/hooks/use-editable-fields";

const phoneUtil = libphonenumber.PhoneNumberUtil.getInstance();

const validatePhoneNumber = (value: string) => {
  try {
    if (!phoneUtil.isValidNumber(phoneUtil.parse(value))) {
      return false;
    }
    return true;
  } catch {
    return false;
  }
};

function AccountSettingsForm({
  account,
  onUpdateKeyValue,
  onUpdateTimezone,
}: {
  account: {
    firstName?: string;
    lastName?: string;
    phone?: string;
    email?: string;
    timezoneId?: Id<"timezones">;
  };
  onUpdateKeyValue: (
    key: "firstName" | "lastName" | "phone",
    value: string,
  ) => Promise<void>;
  onUpdateTimezone?: (timezoneId: Id<"timezones">) => Promise<void>;
}) {
  const { editable, toggleField } = useEditableFields([
    "firstName",
    "lastName",
    "phone",
  ] as const);
  const [firstName, setFirstName] = useState(account.firstName ?? "");
  const [lastName, setLastName] = useState(account.lastName ?? "");
  const [phone, setPhone] = useState(account.phone ?? "");
  const email = account.email ?? "";

  const { data: allTimezones = [] } = useQuery(
    convexQuery(api.account.timezoneQueries.getAllTimezones, {}),
  );

  const [detectedTimezone, setDetectedTimezone] = useState<string | null>(null);
  const [suggestedTimezone, setSuggestedTimezone] = useState<{
    label: string;
    value: Id<"timezones">;
  } | null>(null);

  const timezone = allTimezones.find((tz) => tz.value === account.timezoneId);

  useEffect(() => {
    try {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setDetectedTimezone(userTimezone);

      if (allTimezones.length > 0) {
        const matchingTimezone = allTimezones.find(
          (tz) =>
            tz.label.includes(userTimezone) ||
            userTimezone.includes(tz.label.split(" ")[0]),
        );

        if (matchingTimezone && matchingTimezone.value !== account.timezoneId) {
          setSuggestedTimezone(matchingTimezone);
        }
      }
    } catch {
      // Timezone detection not available
    }
  }, [allTimezones, account.timezoneId]);

  const { open: openTimezoneSearch } = useSheet<
    TimezoneSearchSheetPayload,
    TimezoneSearchSheetResult
  >(SheetIds.TIMEZONE_SEARCH);

  const handleTimezoneOpen = useCallback(async () => {
    const result = await openTimezoneSearch({
      preSelectedOption: account.timezoneId ?? undefined,
    });
    if (result?.selectedOption && onUpdateTimezone) {
      await onUpdateTimezone(result.selectedOption);
      setSuggestedTimezone(null);
    }
  }, [openTimezoneSearch, account.timezoneId, onUpdateTimezone]);

  const handleSetSuggestedTimezone = useCallback(async () => {
    if (!suggestedTimezone || !onUpdateTimezone) return;
    await onUpdateTimezone(suggestedTimezone.value);
    setSuggestedTimezone(null);
  }, [suggestedTimezone, onUpdateTimezone]);

  const disableSaveFirstName = useMemo(
    () => editable.firstName && !firstName.trim(),
    [editable.firstName, firstName],
  );
  const disableSaveLastName = useMemo(
    () => editable.lastName && !lastName.trim(),
    [editable.lastName, lastName],
  );
  const disableSavePhone = useMemo(
    () => editable.phone && (!phone.trim() || !validatePhoneNumber(phone)),
    [editable.phone, phone],
  );

  const saveField = useCallback(
    async (field: "firstName" | "lastName" | "phone", value: string) => {
      await onUpdateKeyValue(field, value);
      toggleField(field);
    },
    [toggleField, onUpdateKeyValue],
  );

  return (
    <View className="gap-4">
      <View className="gap-2">
        <View className="flex-row items-center justify-between">
          <Text className="font-geologica-medium text-base leading-[1.5] text-gray-800">
            First name
          </Text>
          <Pressable
            onPress={() =>
              editable.firstName
                ? saveField("firstName", firstName)
                : toggleField("firstName")
            }
            style={{
              opacity: disableSaveFirstName ? 0.5 : 1,
            }}
            disabled={disableSaveFirstName}
          >
            <Text className="font-geologica-medium text-base leading-[1.5] text-teal-600">
              {editable.firstName ? "Save" : "Edit"}
            </Text>
          </Pressable>
        </View>
        <View pointerEvents={editable.firstName ? "auto" : "none"}>
          <Input
            size="md"
            placeholder="First name"
            value={firstName}
            onChangeText={setFirstName}
            editable={editable.firstName}
            className={editable.firstName ? "opacity-100" : "opacity-50"}
            error={disableSaveFirstName}
            onSubmitEditing={() => saveField("firstName", firstName)}
          />
        </View>
      </View>

      <View className="gap-2">
        <View className="flex-row items-center justify-between">
          <Text className="font-geologica-medium text-base leading-[1.5] text-gray-800">
            Last name
          </Text>
          <Pressable
            onPress={() =>
              editable.lastName
                ? saveField("lastName", lastName)
                : toggleField("lastName")
            }
            style={{
              opacity: disableSaveLastName ? 0.5 : 1,
            }}
            disabled={disableSaveLastName}
          >
            <Text className="font-geologica-medium text-base leading-[1.5] text-teal-600">
              {editable.lastName ? "Save" : "Edit"}
            </Text>
          </Pressable>
        </View>
        <View pointerEvents={editable.lastName ? "auto" : "none"}>
          <Input
            size="md"
            placeholder="Last name"
            value={lastName}
            onChangeText={setLastName}
            editable={editable.lastName}
            className={editable.lastName ? "opacity-100" : "opacity-50"}
            error={disableSaveLastName}
            onSubmitEditing={() => saveField("lastName", lastName)}
          />
        </View>
      </View>

      <View className="gap-2">
        <View className="flex-row items-center justify-between">
          <Text className="font-geologica-medium text-base leading-[1.5] text-gray-800">
            Phone number
          </Text>
          <Pressable
            onPress={() =>
              editable.phone ? saveField("phone", phone) : toggleField("phone")
            }
            style={{
              opacity: disableSavePhone ? 0.5 : 1,
            }}
            disabled={disableSavePhone}
          >
            <Text className="font-geologica-medium text-base leading-[1.5] text-teal-600">
              {editable.phone ? "Save" : "Edit"}
            </Text>
          </Pressable>
        </View>
        <View pointerEvents={editable.phone ? "auto" : "none"}>
          <Input
            size="md"
            placeholder="Phone number"
            value={phone}
            onChangeText={setPhone}
            editable={editable.phone}
            className={editable.phone ? "opacity-100" : "opacity-50"}
            error={disableSavePhone}
            onSubmitEditing={() => saveField("phone", phone)}
          />
        </View>
        <Text className="font-geologica-regular text-sm leading-[1.5] text-black">
          Only shared with our Admin team
        </Text>
      </View>

      <View className="gap-2">
        <View className="flex-row items-center justify-between">
          <Text className="font-geologica-medium text-base leading-[1.5] text-gray-800">
            Email address
          </Text>
          <Text
            className="font-geologica-medium text-base leading-[1.5] text-teal-600"
            onPress={contactSupport}
          >
            Contact admin to change
          </Text>
        </View>
        <View pointerEvents={"none"}>
          <Input
            size="md"
            placeholder="Email address"
            value={email}
            editable={false}
            className="opacity-50"
          />
        </View>
      </View>

      <View className="gap-2">
        <View className="flex-row items-center justify-between">
          <Text className="font-geologica-medium text-base leading-[1.5] text-gray-800">
            Time-zone
          </Text>
          <Pressable onPress={handleTimezoneOpen}>
            <Text className="font-geologica-medium text-base leading-[1.5] text-teal-600">
              Edit
            </Text>
          </Pressable>
        </View>

        {suggestedTimezone && (
          <View className="gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <Text className="text-sm text-blue-700 font-geologica-medium">
              Detected timezone: {detectedTimezone}
            </Text>
            <Text className="text-sm text-blue-600 font-geologica-regular">
              Set your timezone to {suggestedTimezone.label}?
            </Text>
            <View className="flex-row gap-2">
              <Button
                size="sm"
                intent="primary"
                onPress={handleSetSuggestedTimezone}
              >
                <Button.Text size="sm">Yes, use this timezone</Button.Text>
              </Button>
              <Button
                size="sm"
                intent="secondary"
                onPress={() => setSuggestedTimezone(null)}
              >
                <Button.Text size="sm">No thanks</Button.Text>
              </Button>
            </View>
          </View>
        )}

        {timezone ? (
          <Tag size="md" className="self-start">
            <Tag.Text size="md" numberOfLines={0}>
              {timezone.label}
            </Tag.Text>
          </Tag>
        ) : (
          <View pointerEvents="none">
            <Input
              size="md"
              placeholder="No timezone set"
              value=""
              editable={false}
              className="opacity-50"
            />
          </View>
        )}
      </View>
    </View>
  );
}

function TutorAccountSettingsForm() {
  const { data: account } = useQuery(
    convexQuery(api.tutor.account.queries.getAccountOfCurrentUser, {}),
  );
  const { mutateAsync: updateInfo } = useMutation({
    mutationFn: useConvexMutation(api.tutor.account.mutations.updateInfo),
  });

  const onUpdateKeyValue = useCallback(
    async (key: "firstName" | "lastName" | "phone", value: string) => {
      updateInfo({
        [key]: value,
      });
    },
    [updateInfo],
  );

  const onUpdateTimezone = useCallback(
    async (timezoneId: Id<"timezones">) => {
      await updateInfo({ timezoneId });
    },
    [updateInfo],
  );

  if (!account) {
    return <Fragment />;
  }

  return (
    <AccountSettingsForm
      account={account}
      onUpdateKeyValue={onUpdateKeyValue}
      onUpdateTimezone={onUpdateTimezone}
    />
  );
}

function ParentAccountSettingsForm() {
  const { data: account } = useQuery(
    convexQuery(api.parent.account.queries.getAccountOfCurrentUser, {}),
  );
  const { mutateAsync: updateInfo } = useMutation({
    mutationFn: useConvexMutation(api.parent.account.mutations.updateInfo),
  });

  const onUpdateKeyValue = useCallback(
    async (key: "firstName" | "lastName" | "phone", value: string) => {
      updateInfo({
        [key]: value,
      });
    },
    [updateInfo],
  );

  const onUpdateTimezone = useCallback(
    async (timezoneId: Id<"timezones">) => {
      await updateInfo({ timezoneId });
    },
    [updateInfo],
  );

  if (!account) {
    return <Fragment />;
  }

  return (
    <AccountSettingsForm
      account={account}
      onUpdateKeyValue={onUpdateKeyValue}
      onUpdateTimezone={onUpdateTimezone}
    />
  );
}

function AccountSettingsScreen() {
  const { data: currentUser, isSuccess: isCurrentUserSuccess } = useQuery(
    convexQuery(api.account.queries.getCurrentUser, {}),
  );
  const persona = currentUser?.persona;

  if (!isCurrentUserSuccess) {
    return <Loader />;
  }

  return (
    <View className="flex-1 bg-teachLoop-creamBase">
      <AppBar>
        <AppBar.Icon onPress={goBack} />
        <AppBar.Title>Back</AppBar.Title>
      </AppBar>

      <ScrollView showsVerticalScrollIndicator={false}>
        <ResponsiveContent>
          <View className="mx-5">
            <View className="h-2" />
            <View className="gap-1">
              <Text className="font-zilla-slab-medium font-medium text-dark text-[28px] leading-[1.2]">
                Account settings
              </Text>
            </View>

            <View className="h-9" />

            {persona === "tutor" ? (
              <TutorAccountSettingsForm />
            ) : persona === "parent" ? (
              <ParentAccountSettingsForm />
            ) : (
              <Fragment />
            )}

            <View className="h-9" />
          </View>
        </ResponsiveContent>
      </ScrollView>
    </View>
  );
}

export default AccountSettingsScreen;
